import React, { useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Input,
  Modal,
  NativeSelect,
  Rating,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import Fa from 'components/fa';
import { CreatorAdminFeedbackStore } from '@seedlang/stores';

const CONTACT_TYPE_OPTIONS = [
  { value: '', label: '' },
  { value: 'bug', label: 'Bug' },
  { value: 'small_improvement', label: 'Small improvement' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'something_else', label: 'Something else' },
];

const ContactUsActionIconFa = styled.span`
  .fa {
    font-size: 20px;
    font-weight: bold;
  }
`;

const SUCCESS_MODAL_SPACING = 40;

const successIconTwist = keyframes`
  0% {
    opacity: 0;
    transform: rotate(-28deg) scale(0.82);
  }
  55% {
    transform: rotate(10deg) scale(1.03);
  }
  78% {
    transform: rotate(-3deg) scale(1);
  }
  100% {
    opacity: 1;
    transform: rotate(0deg) scale(1);
  }
`;

const successTextFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SuccessModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SUCCESS_MODAL_SPACING}px;
  padding-top: ${SUCCESS_MODAL_SPACING}px;
`;

const SuccessCheckIcon = styled.span`
  display: inline-block;
  animation: ${successIconTwist} 0.7s ease-out 0.15s forwards;

  .fa {
    color: #51cf66;
    font-size: 72px;
    line-height: 1;
  }
`;

const SuccessTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  opacity: 0;
  animation: ${successTextFadeIn} 0.25s ease-out forwards;
`;

const SuccessThankYouText = styled.div`
  font-size: 38px;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
`;

const SuccessSubtitle = styled.div`
  font-size: 14px;
  line-height: 1.35;
  text-align: center;
  color: var(--mantine-color-dimmed);
`;

const RATING_CIRCLE_SIZE = 25;

const RatingEmptyCircle = styled.span`
  display: inline-block;
  width: ${RATING_CIRCLE_SIZE}px;
  height: ${RATING_CIRCLE_SIZE}px;
  border-radius: 50%;
  background-color: #dee2e6;
`;

const RatingFullCircle = styled.span`
  display: inline-block;
  width: ${RATING_CIRCLE_SIZE}px;
  height: ${RATING_CIRCLE_SIZE}px;
  border-radius: 50%;
  background-color: #4dabf7;
`;

const ImportanceRatingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 320px;
`;

const FullWidthRatingWrapper = styled.div`
  width: 100%;

  & > div {
    width: 100% !important;
    max-width: 100% !important;
    justify-content: space-between !important;
  }
`;

export default function CreatorContactUs({ showAdminsOnlyInfo }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successAnimationKey, setSuccessAnimationKey] = useState(0);
  const [contactType, setContactType] = useState('');
  const [importance, setImportance] = useState(0);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSend = contactType !== ''
    && importance > 0
    && subject.trim() !== ''
    && message.trim() !== '';
  const contactTitle = showAdminsOnlyInfo ? 'Contact Us (Admins Only)' : 'Contact Us';

  const resetForm = () => {
    setContactType('');
    setImportance(0);
    setSubject('');
    setMessage('');
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const closeSuccessModal = () => {
    setSuccessModalOpen(false);
  };

  const onSend = () => {
    if (!canSend || submitting) {
      return;
    }

    setSubmitting(true);
    CreatorAdminFeedbackStore.create({
      data: {
        type: contactType,
        importance,
        subject: subject.trim(),
        message: message.trim(),
        url: window.location.href,
      },
      failureCallback: () => setSubmitting(false),
    }, (resp) => {
      setSubmitting(false);
      if (resp?.success) {
        setModalOpen(false);
        resetForm();
        setSuccessAnimationKey((key) => key + 1);
        setSuccessModalOpen(true);
      }
    });
  };

  return (
    <>
      <Box pos='fixed' bottom={32} right={32} style={{ zIndex: 200 }}>
        <Tooltip label={contactTitle} withArrow={false} position='top-end'>
          <ActionIcon
            size='xl'
            variant='filled'
            onClick={() => setModalOpen(true)}
          >
            <ContactUsActionIconFa>
              <Fa i='comment-o' />
            </ContactUsActionIconFa>
          </ActionIcon>
        </Tooltip>
      </Box>
      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title={contactTitle}
        size='lg'
      >
        <Stack gap='md'>
          <Text c='dimmed' size='sm'>
            We'd love to hear any feedback that you might have about using the membership platform! Your feedback will help us with planning new features and making improvements, and is very much appreciated.
          </Text>
          <NativeSelect
            label='Type'
            data={CONTACT_TYPE_OPTIONS}
            value={contactType}
            onChange={(e) => setContactType(e.currentTarget.value)}
          />
          <ImportanceRatingSection>
            <Input.Label>How important is this to you?</Input.Label>
            <FullWidthRatingWrapper>
              <Rating
                count={10}
                value={importance}
                onChange={setImportance}
                emptySymbol={<RatingEmptyCircle />}
                fullSymbol={<RatingFullCircle />}
              />
            </FullWidthRatingWrapper>
          </ImportanceRatingSection>
          <TextInput
            label='Subject'
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
          />
          <Textarea
            label='Message'
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
          />
          <Button
            fullWidth
            disabled={!canSend}
            loading={submitting}
            leftSection={<Fa i='paper-plane-o' />}
            onClick={onSend}
          >
            Send
          </Button>
        </Stack>
      </Modal>
      <Modal
        opened={successModalOpen}
        onClose={closeSuccessModal}
        title='Success'
        size='lg'
      >
        <SuccessModalLayout key={successAnimationKey}>
          <Center>
            <Stack gap='lg' align='center'>
              <SuccessCheckIcon>
                <Fa i='check-circle' />
              </SuccessCheckIcon>
              <SuccessTextGroup>
                <SuccessThankYouText>Thank you!</SuccessThankYouText>
                <SuccessSubtitle>
                  We appreciate your feedback and will reach out if we have any questions.
                </SuccessSubtitle>
              </SuccessTextGroup>
            </Stack>
          </Center>
          <Button fullWidth onClick={closeSuccessModal}>
            Close
          </Button>
        </SuccessModalLayout>
      </Modal>
    </>
  );
}
